import http from "@/utils/axios";
import { notify } from "@kyvg/vue3-notification";

export default {
  namespaced: true,
  state: {
    referrals: [],
    pagination: {
      currentPage: 1,
      total: 1,
    },
    currentReferral: null,
    stats: {
      referrals_count: 0,
      avg_referrals_count: 0,
      avg_referrals_price: 0,
    },
    commissionRange: [1, 100],
    priceRange: [1, 1000],
    patientsOptions: [],
    maxQuantityOfTests: 100,
    maxTestPrice: 2000,
    isLoadingReferrals: false,
  },
  getters: {
    getReferrals: (state) => state.referrals,
    getReferralById: (state) => (id) => {
      return state.referrals.find((referral) => referral.id === id);
    },
    getPagination: (state) => state.pagination,
    getCurrentReferral: (state) => state.currentReferral,
    getStats: (state) => state.stats,
    getCommissionRange: (state) => state.commissionRange,
    getPriceRange: (state) => state.priceRange,
    getMaxQuantityOfTests: (state) => state.maxQuantityOfTests,
    getMaxTestPrice: (state) => state.maxTestPrice,
    getPatientsOptions: (state) =>
      state.patientsOptions.map((patient) => ({
        id: patient.id,
        name: patient.full_name,
      })),
    isLoadingReferrals: (state) => state.isLoadingReferrals,
  },
  mutations: {
    setReferrals(state, referrals) {
      state.referrals = referrals.data;
      state.pagination.currentPage = referrals.meta.current_page;
      state.pagination.total = referrals.meta.last_page;
    },
    setCurrentPage(state, currentPage) {
      state.pagination.currentPage = currentPage;
    },
    setCurrentReferral(state, currentReferral) {
      state.currentReferral = currentReferral;
    },
    setCurrentReferralPatient(state, patient) {
      state.currentReferral.patient = patient;
    },
    setStats(state, stats) {
      state.stats = stats;
    },
    setCommissionRange(state, commissionRange) {
      state.commissionRange = commissionRange;
    },
    setPriceRange(state, priceRange) {
      state.priceRange = priceRange;
    },
    setMaxQuantityOfTests(state, quantity) {
      state.maxQuantityOfTests = quantity;
    },
    setPatientsOptions(state, patients) {
      state.patientsOptions = patients;
    },
    setMaxTestPrice(state, price) {
      state.maxTestPrice = price;
    },
    clearReferrals(state) {
      state.referrals = [];
      state.pagination.currentPage = 1;
      state.pagination.total = 1;
    },
    clearCurrentReferral(state) {
      state.currentReferral = null;
    },
  },
  actions: {
    async fetchReferrals(
      { commit, state },
      { time, date_range, filters, sortBy } = {
        time: null,
        date_range: null,
        filters: null,
        sortBy: null,
      },
    ) {
      const { data } = await http.get(`/referral`, {
        params: {
          page: state.pagination.currentPage,
          time: time,
          date_range: date_range,
          ...filters,
          ...sortBy,
        },
      });
      commit("setReferrals", data);
      // commit("user/setCurrentUserStats", data.stats, { root: true });
      // let userRole = rootGetters["user/getCurrentUserRole"];
      // if (userRole != "patient")
      //   commit("referrals/setCommissionRange", data.commission_range, {
      //     root: true,
      //   });
    },
    async checkResults(
      { commit, state },
      { id, time = "current_month", date_range = null, filters, sortBy },
    ) {
      const { data } = await http.get(`referrals/check-results/${id}`, {
        params: {
          page: state.pagination.currentPage,
          time: time,
          date_range: date_range,
          ...filters,
          ...sortBy,
        },
      });
      commit("setReferrals", data.referrals);
    },
    async fetchDoctorReferrals(
      { commit, state },
      { time, date_range, filters, sortBy, search } = {},
    ) {
      const { data } = await http.get("referrals/all-referrals", {
        params: {
          page: state.pagination.currentPage,
          time,
          date_range,
          search,
          ...filters,
          ...sortBy,
        },
      });
      commit("setReferrals", data.referrals);
      commit("setStats", data.stats);
      commit("doctors/setTopDoctors", data.top_doctors, { root: true });
      commit("referrals/setPriceRange", data.price_range, {
        root: true,
      });
    },
    async fetchPatientsOptions({ commit }) {
      const response = await http.get("doctor/filters/patients");
      commit("setPatientsOptions", response.data);
    },
    async fetchMaxQuantityOfTests({ commit }, patientId = null) {
      const response = await http.get("doctor/filters/quantity-of-tests", {
        params: {
          patient_id: patientId,
        },
      });
      commit("setMaxQuantityOfTests", response.data);
    },
    async fetchMaxTestPrice({ commit }, patientId = null) {
      const response = await http.get("doctor/filters/test-price", {
        params: {
          patient_id: patientId,
        },
      });
      commit("setMaxTestPrice", response.data);
    },
    async fetchAdminReferrals(
      { commit, state },
      { time = "current_month", date_range = null, filters, sortBy },
    ) {
      const { data } = await http.get("admin/referrals", {
        params: {
          page: state.pagination.currentPage,
          time: time,
          date_range: date_range,
          ...filters,
          ...sortBy,
        },
      });
      commit("setReferrals", data.referrals);
      commit("setStats", data.stats);
      commit("doctors/setTopDoctors", data.top_doctors, { root: true });
      commit("referrals/setPriceRange", data.price_range, {
        root: true,
      });
    },
    async fetchCurrentReferral({ commit }, id) {
      return await http
        .get(`/referrals/${id}`)
        .then(({ data }) => {
          commit("setCurrentReferral", data.referral);
        })
        .catch(({ response }) => {
          if (response && response.status === 403)
            notify({
              type: "danger",
              text: "messages.permission.failure",
              duration: 5000,
            });
        });
    },
    sendAndDownloadReferral({ state }) {
      const windowReference = window.open();

      return http({
        url: `/referrals/send-and-download/${state.currentReferral.id}`,
        method: "POST",
      })
        .then((response) => {
          windowReference.location = response.data.url;
        })
        .catch(({ response }) => {
          if (response && response.status === 403)
            notify({
              type: "danger",
              text: "messages.permission.forbidden",
              duration: 5000,
              ignoreDuplicates: true,
            });
        });
    },
  },
};
